import { useEffect, useState } from 'react';
import { useTheme } from 'components/core/theme-provider';

const ANIMATION_CONFIG = {
  numLayers: 60,
  animationDelay: 150,
  visibleWindowSize: 32,
  rotationPerLayer: 2.5,
  minSizeRatio: 0.15,
  sizeGrowthRatio: 1.35,
  strokeWidth: 0.40,
  transitionDuration: '0.6s',
} as const;

type GeometricAnimationProps = Readonly<{
  className?: string;
}>;

export const GeometricAnimation = ({ className = '' }: GeometricAnimationProps) => {
  const { resolvedTheme } = useTheme();
  const [animationPhase, setAnimationPhase] = useState(0);
  const isDarkMode = resolvedTheme === 'dark';

  const {
    numLayers,
    animationDelay,
    visibleWindowSize,
    rotationPerLayer,
    minSizeRatio,
    sizeGrowthRatio,
    strokeWidth,
    transitionDuration,
  } = ANIMATION_CONFIG;

  useEffect(() => {
    const interval = window.setInterval(() => {
      setAnimationPhase((prev) => (prev + 1) % (numLayers * 2));
    }, animationDelay);

    return () => window.clearInterval(interval);
  }, [numLayers, animationDelay]);

  const getLayerOpacity = (layerIndex: number) => {
    const windowCenter = animationPhase % numLayers;
    let distance = Math.abs(layerIndex - windowCenter);

    if (distance > numLayers / 2) {
      distance = numLayers - distance;
    }

    if (distance <= visibleWindowSize / 2) {
      const normalizedDistance = distance / (visibleWindowSize / 2);
      const opacity = 1 - normalizedDistance * normalizedDistance * 0.85;
      return Math.max(0.1, opacity);
    }

    return 0.05;
  };

  const generateTwistedTrianglePath = (
    centerX: number,
    centerY: number,
    size: number,
    rotationDeg: number
  ) => {
    const corners = [{ angle: -90 }, { angle: 30 }, { angle: 150 }];

    const points = corners.map((corner) => {
      const angle = ((corner.angle + rotationDeg) * Math.PI) / 180;
      return {
        x: centerX + size * Math.cos(angle),
        y: centerY + size * Math.sin(angle),
      };
    });

    return `
      M ${(points[0].x + points[2].x) / 2} ${(points[0].y + points[2].y) / 2}
      Q ${points[0].x} ${points[0].y}, ${(points[0].x + points[1].x) / 2} ${(points[0].y + points[1].y) / 2}
      Q ${points[1].x} ${points[1].y}, ${(points[1].x + points[2].x) / 2} ${(points[1].y + points[2].y) / 2}
      Q ${points[2].x} ${points[2].y}, ${(points[0].x + points[2].x) / 2} ${(points[0].y + points[2].y) / 2}
      Z
    `;
  };

  const generatePattern = (
    centerX: number,
    centerY: number,
    baseSize: number,
    patternId: string
  ) => {
    const paths = [];

    for (let i = 0; i < numLayers; i++) {
      const progress = i / numLayers;
      const size = baseSize * (minSizeRatio + progress * sizeGrowthRatio);
      const rotation = i * rotationPerLayer;
      const opacity = getLayerOpacity(i);

      let strokeColor: string;
      if (isDarkMode) {
        const grayValue = Math.round(190 - progress * 90);
        strokeColor = `rgba(${grayValue}, ${grayValue}, ${grayValue}, ${opacity})`;
      } else {
        const r = Math.round(180 - progress * 180);
        const g = Math.round(195 - progress * 93);
        const b = Math.round(230 - progress * 52);
        strokeColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }

      paths.push(
        <path
          key={`${patternId}-${i}`}
          d={generateTwistedTrianglePath(centerX, centerY, size, rotation)}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          style={{
            transition: `stroke ${transitionDuration} ease-out`,
          }}
        />
      );
    }

    return paths;
  };

  return (
    <div
      className={`relative h-full w-full overflow-hidden ${isDarkMode ? 'bg-[#1a2740]' : 'bg-neutral-50'
        } ${className}`}
    >
      <svg
        viewBox="0 0 400 900"
        className="h-full w-full"
        preserveAspectRatio="xMidYMid slice"
      >
        {generatePattern(365, 150, 370, 'top-right')}
        {generatePattern(35, 750, 370, 'bottom-left')}
      </svg>
    </div>
  );
};

export default GeometricAnimation;
